/**
 * Dataverse row CRUD tools
 */

import { z } from "zod";
import { displayValue } from "../utils/format.js";
import type { DataverseApi } from "../api/dataverse-api.js";
import { logger } from "../utils/logger.js";
import { ConfirmationRequiredError } from "../utils/errors.js";

// =============================================================================
// Tool Definitions
// =============================================================================

export const queryDataverseRowsTool = {
  name: "query_dataverse_rows",
  description:
    "Query rows from a Dataverse table with OData filtering, selecting, and ordering. Use list_dataverse_tables first to get the EntitySetName. Returns matching rows with their field values.",
  inputSchema: {
    type: "object" as const,
    properties: {
      entitySetName: {
        type: "string",
        description: "The entity set name (e.g., 'accounts', 'contacts'). Get this from list_dataverse_tables.",
      },
      select: {
        type: "string",
        description: "Comma-separated column names to return (e.g., 'name,emailaddress1,telephone1')",
      },
      filter: {
        type: "string",
        description: "OData filter expression (e.g., \"name eq 'Contoso'\" or \"revenue gt 1000000\")",
      },
      orderBy: {
        type: "string",
        description: "OData order by (e.g., 'name asc' or 'createdon desc')",
      },
      top: {
        type: "number",
        description: "Maximum number of rows to return (default 50, max 250)",
        minimum: 1,
        maximum: 250,
      },
      expand: {
        type: "string",
        description: "OData expand for related entities (e.g., 'primarycontactid')",
      },
    },
    required: ["entitySetName"],
  },
};

export const getDataverseRowTool = {
  name: "get_dataverse_row",
  description:
    "Get a single Dataverse row by its ID. Returns all fields or selected fields for the specified row.",
  inputSchema: {
    type: "object" as const,
    properties: {
      entitySetName: {
        type: "string",
        description: "The entity set name (e.g., 'accounts', 'contacts')",
      },
      id: {
        type: "string",
        description: "The row ID (GUID)",
      },
      select: {
        type: "string",
        description: "Comma-separated column names to return",
      },
      expand: {
        type: "string",
        description: "OData expand for related rows, e.g. 'parentcustomerid_account($select=name)'",
      },
    },
    required: ["entitySetName", "id"],
  },
};

export const createDataverseRowTool = {
  name: "create_dataverse_row",
  description:
    "Create a new row in a Dataverse table. Use get_dataverse_table first to see available columns and required fields. Returns the created row with its new ID.",
  inputSchema: {
    type: "object" as const,
    properties: {
      entitySetName: {
        type: "string",
        description: "The entity set name (e.g., 'accounts', 'contacts')",
      },
      data: {
        type: "object",
        description: "The field values for the new row (e.g., {\"name\": \"Contoso\", \"telephone1\": \"555-0100\"})",
        additionalProperties: true,
      },
    },
    required: ["entitySetName", "data"],
  },
};

export const updateDataverseRowTool = {
  name: "update_dataverse_row",
  description:
    "Update an existing Dataverse row. Only include fields you want to change. The row must exist.",
  inputSchema: {
    type: "object" as const,
    properties: {
      entitySetName: {
        type: "string",
        description: "The entity set name (e.g., 'accounts', 'contacts')",
      },
      id: {
        type: "string",
        description: "The row ID (GUID) to update",
      },
      data: {
        type: "object",
        description: "The field values to update (e.g., {\"telephone1\": \"555-0200\"})",
        additionalProperties: true,
      },
    },
    required: ["entitySetName", "id", "data"],
  },
};

export const deleteDataverseRowTool = {
  name: "delete_dataverse_row",
  description:
    "Delete a Dataverse row permanently. This action cannot be undone. You must set confirm=true to proceed with deletion.",
  inputSchema: {
    type: "object" as const,
    properties: {
      entitySetName: {
        type: "string",
        description: "The entity set name (e.g., 'accounts', 'contacts')",
      },
      id: {
        type: "string",
        description: "The row ID (GUID) to delete",
      },
      confirm: {
        type: "boolean",
        description: "Set to true to confirm deletion. Required to prevent accidental deletions.",
      },
    },
    required: ["entitySetName", "id"],
  },
};

// =============================================================================
// Input Schemas
// =============================================================================

export const queryDataverseRowsInputSchema = z.object({
  entitySetName: z.string().min(1),
  select: z.string().optional(),
  filter: z.string().optional(),
  orderBy: z.string().optional(),
  top: z.number().min(1).max(250).optional(),
  expand: z.string().optional(),
});

export const getDataverseRowInputSchema = z.object({
  entitySetName: z.string().min(1),
  id: z.string().min(1),
  select: z.string().optional(),
  expand: z.string().optional(),
});

export const createDataverseRowInputSchema = z.object({
  entitySetName: z.string().min(1),
  data: z.record(z.string(), z.unknown()),
});

export const updateDataverseRowInputSchema = z.object({
  entitySetName: z.string().min(1),
  id: z.string().min(1),
  data: z.record(z.string(), z.unknown()),
});

export const deleteDataverseRowInputSchema = z.object({
  entitySetName: z.string().min(1),
  id: z.string().min(1),
  confirm: z.boolean().optional(),
});

// =============================================================================
// Tool Handlers
// =============================================================================

export interface DataverseRowToolsContext {
  api: DataverseApi | null;
}

function getDataverseNotAvailableMessage(): string {
  const initError = (globalThis as Record<string, unknown>).__dataverseInitError;
  const reason = initError ? `\n\nInit error: ${displayValue(initError)}` : "";
  return `Dataverse not available for this environment.

This typically means:
1. The environment doesn't have Dataverse provisioned
2. It's a "Default" environment without a Dataverse database

To use Dataverse row operations:
- Create an environment with a Dataverse database in Power Platform admin center${reason}`;
}

/** Field names a caller named in `$select`, lowercased. */
export function selectedFieldSet(select: string | undefined): Set<string> {
  if (!select) return new Set();
  return new Set(
    select
      .split(",")
      .map((field) => field.trim().toLowerCase())
      .filter((field) => field !== ""),
  );
}

/**
 * Render one row.
 *
 * Lookup columns (`_x_value`) are the FOREIGN KEY — the only way to follow a
 * relationship — so they are hidden only when the caller did not ask for
 * them. Dataverse attaches one to most rows whether or not it is wanted, and
 * suppressing that noise was the original intent; suppressing a column the
 * caller explicitly `$select`ed was not, and made it impossible to answer
 * questions like "which solution is this agent in?" from the output.
 *
 * The annotations are the metadata, not the value: `@OData…FormattedValue`
 * carries the display name and is folded into its own field's line, while
 * `associatednavigationproperty` and `lookuplogicalname` are dropped. Before
 * this, every one of those printed while the id they describe did not.
 */
function formatRow(row: Record<string, unknown>, selected: Set<string> = new Set()): string {
  const lines: string[] = [];
  const formatted = new Map<string, string>();
  for (const [key, value] of Object.entries(row)) {
    const marker = key.indexOf("@");
    if (marker <= 0) continue;
    if (!key.endsWith("@OData.Community.Display.V1.FormattedValue")) continue;
    if (typeof value === "string" && value !== "") formatted.set(key.slice(0, marker), value);
  }

  for (const [key, value] of Object.entries(row)) {
    // Annotations and OData control fields are metadata about a column, not a column.
    if (key.startsWith("@") || key.includes("@")) continue;
    const isLookup = key.startsWith("_") && key.endsWith("_value");
    if (isLookup && !selected.has(key.toLowerCase())) continue;
    if (value === null || value === undefined) continue;
    const label = formatted.get(key);
    const rendered = displayValue(value);
    lines.push(
      label !== undefined && label !== rendered
        ? `    ${key}: ${rendered} (${label})`
        : `    ${key}: ${rendered}`,
    );
  }
  return lines.join("\n");
}

export async function handleQueryDataverseRows(
  context: DataverseRowToolsContext,
  input: unknown
): Promise<string> {
  if (!context.api) return getDataverseNotAvailableMessage();

  const parsed = queryDataverseRowsInputSchema.parse(input);
  logger.info({ entitySetName: parsed.entitySetName }, "Executing query_dataverse_rows");

  const result = await context.api.queryRows(parsed.entitySetName, {
    select: parsed.select,
    filter: parsed.filter,
    orderBy: parsed.orderBy,
    top: parsed.top,
    expand: parsed.expand,
  });

  if (result.value.length === 0) {
    return `No rows found in ${parsed.entitySetName}${parsed.filter ? ` matching filter: ${parsed.filter}` : ""}.`;
  }

  const lines = [`Results from ${parsed.entitySetName} (${result.value.length} rows):`];
  for (let i = 0; i < result.value.length; i++) {
    const row = result.value[i];
    if (!row) continue;
    lines.push(`\n  Row ${i + 1}:`);
    lines.push(formatRow(row, selectedFieldSet(parsed.select)));
  }

  if (result.nextLink) {
    lines.push("\n  [More rows available — increase $top or refine $filter]");
  }

  return lines.join("\n");
}

export async function handleGetDataverseRow(
  context: DataverseRowToolsContext,
  input: unknown
): Promise<string> {
  if (!context.api) return getDataverseNotAvailableMessage();

  const parsed = getDataverseRowInputSchema.parse(input);
  logger.info({ entitySetName: parsed.entitySetName, id: parsed.id }, "Executing get_dataverse_row");

  const row = await context.api.getRow(parsed.entitySetName, parsed.id, parsed.select, parsed.expand);

  const lines = [`Row from ${parsed.entitySetName} (${parsed.id}):`];
  lines.push(formatRow(row, selectedFieldSet(parsed.select)));
  return lines.join("\n");
}

export async function handleCreateDataverseRow(
  context: DataverseRowToolsContext,
  input: unknown
): Promise<string> {
  if (!context.api) return getDataverseNotAvailableMessage();

  const parsed = createDataverseRowInputSchema.parse(input);
  logger.info({ entitySetName: parsed.entitySetName }, "Executing create_dataverse_row");

  const row = await context.api.createRow(parsed.entitySetName, parsed.data);

  // Lead with the new row's id. The full row dump lists a dozen GUIDs
  // (address ids, owner, business unit) before the primary key, and a caller
  // that grabs "the first GUID" gets the wrong one — seen live, it then
  // tried to delete an address id and reported the row as nonexistent.
  const primaryId = primaryIdOf(parsed.entitySetName, row);
  const lines = [primaryId ? `Created row in ${parsed.entitySetName} — id ${primaryId}` : `Created row in ${parsed.entitySetName}:`];
  lines.push(formatRow(row));
  return lines.join("\n");
}

/** Best-effort primary key of a returned row: `<logicalname>id`, else the first non-lookup *id GUID. */
export function primaryIdOf(entitySetName: string, row: Record<string, unknown>): string | undefined {
  const guid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const set = entitySetName.toLowerCase();
  const candidates = set.endsWith("ies") ? [set.slice(0, -3) + "y"] : set.endsWith("ses") || set.endsWith("xes") ? [set.slice(0, -2), set.slice(0, -1)] : set.endsWith("s") ? [set.slice(0, -1)] : [set];
  for (const logical of candidates) {
    const v = row[`${logical}id`];
    if (typeof v === "string" && guid.test(v)) return v;
  }
  for (const [k, v] of Object.entries(row)) {
    if (/^[a-z][a-z0-9_]*id$/.test(k) && !k.startsWith("_") && !k.includes("@") && typeof v === "string" && guid.test(v) && !/^(address\d_addressid|ownerid|owningbusinessunit|owninguser|transactioncurrencyid)$/.test(k)) {
      return v;
    }
  }
  return undefined;
}

export async function handleUpdateDataverseRow(
  context: DataverseRowToolsContext,
  input: unknown
): Promise<string> {
  if (!context.api) return getDataverseNotAvailableMessage();

  const parsed = updateDataverseRowInputSchema.parse(input);
  logger.info({ entitySetName: parsed.entitySetName, id: parsed.id }, "Executing update_dataverse_row");

  await context.api.updateRow(parsed.entitySetName, parsed.id, parsed.data);

  return `Updated row ${parsed.id} in ${parsed.entitySetName}.\nFields updated: ${Object.keys(parsed.data).join(", ")}`;
}

export async function handleDeleteDataverseRow(
  context: DataverseRowToolsContext,
  input: unknown
): Promise<string> {
  if (!context.api) return getDataverseNotAvailableMessage();

  const parsed = deleteDataverseRowInputSchema.parse(input);

  if (!parsed.confirm) {
    throw new ConfirmationRequiredError(`Deletion not confirmed. Set confirm=true to delete row ${parsed.id} from ${parsed.entitySetName}. This action cannot be undone.`);
  }

  logger.info({ entitySetName: parsed.entitySetName, id: parsed.id }, "Executing delete_dataverse_row");

  await context.api.deleteRow(parsed.entitySetName, parsed.id);

  return `Deleted row ${parsed.id} from ${parsed.entitySetName}.`;
}
